import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../../../../components/ui/select';
import { cn } from '../../../../lib/utils';

import { STRATEGY_SET_TYPE_OPTIONS } from './constants';
import { useStrategySets } from './hooks';

const ALL_OPTION_VALUE = '__all__';

const serializeSelectValue = (value) =>
    value === null || value === undefined ? ALL_OPTION_VALUE : String(value);

const parseNullableValue = (value) => (value === ALL_OPTION_VALUE ? null : value);

const TRADEBOOK_TRIGGER_CLASS_NAME = [
    'journal-filter-tradebook-trigger h-9 w-full rounded-md border-[#E5E5E5]',
    'bg-white px-3 text-sm shadow-[0_1px_2px_rgba(0,0,0,0.05)]',
    'focus:ring-[#2563EB]'
].join(' ');

const renderSelectItems = (options) =>
    options.map((option) => (
        <SelectItem
            key={serializeSelectValue(option.value)}
            value={serializeSelectValue(option.value)}
        >
            {option.label}
        </SelectItem>
    ));

function TradebookField({ label, children }) {
    return (
        <div className="space-y-2">
            <p className="text-sm font-medium leading-5 text-[#0A0A0A]">{label}</p>
            {children}
        </div>
    );
}

TradebookField.propTypes = {
    label: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired
};

/**
 * TradingPlan / Strategy Set filters for TradeBook
 */
export default function TradebookFilters({
    className,
    filterValues,
    onFilterChange,
    tradingPlanOptions
}) {
    const { availableSets, setNumberOptions } = useStrategySets(
        filterValues.tradingPlanId,
        filterValues.strategySetType
    );

    // Reset dependent filters when trading plan changes
    useEffect(() => {
        if (!filterValues.tradingPlanId) {
            if (filterValues.strategySetType !== null) {
                onFilterChange('strategySetType', null);
            }
            if (filterValues.setNumber !== null) {
                onFilterChange('setNumber', null);
            }
        }
    }, [
        filterValues.tradingPlanId,
        filterValues.strategySetType,
        filterValues.setNumber,
        onFilterChange
    ]);

    // Reset set number when strategy set type changes
    useEffect(() => {
        if (filterValues.setNumber !== null && filterValues.tradingPlanId) {
            let validNumbers;
            if (filterValues.strategySetType === 'strategy') {
                validNumbers = availableSets.strategy;
            } else if (filterValues.strategySetType === 'noStrategy') {
                validNumbers = availableSets.noStrategy;
            } else {
                validNumbers = [
                    ...(availableSets.strategy || []),
                    ...(availableSets.noStrategy || [])
                ];
            }

            if (validNumbers && !validNumbers.includes(filterValues.setNumber)) {
                onFilterChange('setNumber', null);
            }
        }
    }, [
        filterValues.strategySetType,
        availableSets,
        filterValues.setNumber,
        filterValues.tradingPlanId,
        onFilterChange
    ]);

    return (
        <div className={cn('space-y-4', className)}>
            <TradebookField label="Trading plan">
                <Select
                    value={serializeSelectValue(filterValues.tradingPlanId)}
                    onValueChange={(value) => {
                        onFilterChange('tradingPlanId', parseNullableValue(value));
                    }}
                >
                    <SelectTrigger
                        className={TRADEBOOK_TRIGGER_CLASS_NAME}
                        aria-label="Select Trading Plan"
                    >
                        <SelectValue placeholder="Trading Plan" />
                    </SelectTrigger>
                    <SelectContent>{renderSelectItems(tradingPlanOptions)}</SelectContent>
                </Select>
            </TradebookField>

            {filterValues.tradingPlanId && (
                <TradebookField label="Set type">
                    <Select
                        value={serializeSelectValue(filterValues.strategySetType)}
                        onValueChange={(value) => {
                            onFilterChange('strategySetType', parseNullableValue(value));
                        }}
                    >
                        <SelectTrigger
                            className={TRADEBOOK_TRIGGER_CLASS_NAME}
                            aria-label="Select Strategy Set Type"
                        >
                            <SelectValue placeholder="Set Type" />
                        </SelectTrigger>
                        <SelectContent>
                            {renderSelectItems(STRATEGY_SET_TYPE_OPTIONS)}
                        </SelectContent>
                    </Select>
                </TradebookField>
            )}

            {filterValues.tradingPlanId && filterValues.strategySetType && (
                <TradebookField label="Set number">
                    <Select
                        disabled={setNumberOptions.length <= 1}
                        value={serializeSelectValue(filterValues.setNumber)}
                        onValueChange={(value) => {
                            const parsedValue = parseNullableValue(value);
                            onFilterChange(
                                'setNumber',
                                parsedValue === null ? null : Number(parsedValue)
                            );
                        }}
                    >
                        <SelectTrigger
                            className={[
                                TRADEBOOK_TRIGGER_CLASS_NAME,
                                'disabled:cursor-not-allowed disabled:opacity-60'
                            ].join(' ')}
                            aria-label="Select Set Number"
                        >
                            <SelectValue placeholder="Set #" />
                        </SelectTrigger>
                        <SelectContent>{renderSelectItems(setNumberOptions)}</SelectContent>
                    </Select>
                </TradebookField>
            )}
        </div>
    );
}

TradebookFilters.propTypes = {
    className: PropTypes.string,
    filterValues: PropTypes.shape({
        tradingPlanId: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
        strategySetType: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
        setNumber: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])])
    }).isRequired,
    onFilterChange: PropTypes.func.isRequired,
    tradingPlanOptions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])])
        })
    ).isRequired
};

TradebookFilters.defaultProps = {
    className: undefined
};
